import { Point, Quadtree, Rectangle } from "./quadtree.js";

export interface GraphLoadResult {
    quadtree    : Quadtree<GraphNode>;
    globalNodes : Map<string, GraphNode>;
    edgesArray  : GraphEdge[];
    boundary: {
        minX    : number;
        minY    : number;
        maxX    : number;
        maxY    : number;
    };
}

export interface GraphNode extends Rectangle {
    type        : "node";
    name        : string;
    connections : Set<GraphNode>;
}

export interface GraphEdge {
    type    : "edge";
    tail    : GraphNode;
    head    : GraphNode;
    points  : Point[];
}

export function useGraphLoad() {
    return (file: Blob): Promise<GraphLoadResult> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = function (e: ProgressEvent<FileReader>) {
                if (e.target?.result) {
                    const lines         = (e.target.result as string).trim().split("\n");
                    const boundary      = { x: 0, y: 0, width: 3_000_000, height: 3_000_000 };
                    const quadtree      = new Quadtree<GraphNode>(boundary, 128);
                    const globalNodes   = new Map<string, GraphNode>;
                    const edgesArray    = [] as GraphEdge[];

                    let maxX = 0;
                    let maxY = 0;
                    let minX = 0;
                    let minY = 0;

                    for (const line of lines) {
                        const parts = line.split(" ");
                        const type = parts[0];

                        if (type === "node") {
                            const [_, name, x, y, width, height] = parts;

                            const node: GraphNode = {
                                type        : "node",
                                name        : name || "",
                                x           : parseFloat(x || ""),
                                y           : parseFloat(y || ""),
                                width       : parseFloat(width || ""),
                                height      : parseFloat(height || ""),
                                connections : new Set(),
                            };

                            if (maxX < node.x)
                                maxX = node.x

                            if (minX > node.x)
                                minX = node.x;

                            if (maxY < node.y)
                                maxY = node.y;

                            if (minY > node.y)
                                minY = node.y;

                            globalNodes.set(node.name, node);
                            quadtree.insert(node);

                        } else if (type === "edge") {
                            const [_, tail, head, nStr, ...coords] = parts;
                            const n         = parseInt(nStr || "");
                            const points    = [] as Point[];

                            for (let i = 0; i < (n * 2); i += 2) {
                                points.push({
                                    x: parseFloat(coords[i] || ""),
                                    y: parseFloat(coords[i + 1] || ""),
                                });
                            }

                            const tailNode = globalNodes.get(tail || "");
                            const headNode = globalNodes.get(head || "");

                            if (!tailNode || !headNode)
                                throw new Error("Un noeud est manquant dans le graph.");

                            tailNode.connections.add(headNode);
                            headNode.connections.add(tailNode);

                            edgesArray.push({
                                type: "edge",
                                tail: tailNode,
                                head: headNode,
                                points,
                            });
                        }
                    }

                    resolve({
                        quadtree,
                        edgesArray,
                        globalNodes,
                        boundary: {
                            minX,
                            minY,
                            maxX,
                            maxY,
                        },
                    });

                } else {
                    reject("TODO");
                }
            };

            reader.readAsText(file);
        });
    };
}

export function findNodeUnderMouse(quadtree: Quadtree<GraphNode>, worldX: number, worldY: number, diameter: number): GraphNode | undefined {
    const searchRange   = { x: worldX - (diameter / 2), y: worldY - (diameter / 2), width: diameter, height: diameter };
    const candidates    = quadtree.query(searchRange);

    candidates.sort((a, b) => {
        const xA = a.x - worldX;
        const yA = a.y - worldY;
        const dA = Math.sqrt(xA * xA + yA * yA);

        const xB = b.x - worldX;
        const yB = b.y - worldY;
        const dB = Math.sqrt(xB * xB + yB * yB);

        return dA - dB;
    });

    return candidates[0];
}
